import { createPinia } from 'pinia';
import VCalendar from 'v-calendar';
import 'v-calendar/style.css';
import { createApp } from 'vue';
import Toast, { POSITION, useToast } from 'vue-toastification';
import 'vue-toastification/dist/index.css';

import { setApiToast } from '@/api/base';
import App from '@/App.vue';
import router from '@/router/router';

import '@/styles/main.scss';

import { Notifier } from '@airbrake/browser';
//Import Mixpanel SDK
import mixpanel from 'mixpanel-browser';
import { setAirbrake } from '@/utils/airbrake';

const toastOptions = {
  position: POSITION.TOP_RIGHT,
  timeout: 5000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: false,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: 'button',
  icon: true,
  maxToasts: 3,
  newestOnTop: true
};
const pinia = createPinia();
const app = createApp(App);

const createDummyAirbrake = () => ({
  notify: (notice: { error?: Error | string; params?: Record<string, unknown> } | Error) => {
    if (notice instanceof Error) {
      console.error(`[Airbrake] Notifying error: ${notice.message}`);
      return;
    }
    const message =
      notice.error instanceof Error
        ? notice.error.message
        : typeof notice.error === 'string'
          ? notice.error
          : 'unknown';
    console.error(`[Airbrake] Notifying error: ${message}`, notice.params ?? {});
  },
  addFilter: (filter: (notice: unknown) => boolean) => {
    console.log(`[Airbrake] Adding filter: ${filter.name}`);
  }
});

const createAirbrakeFromEnv = (): Notifier | ReturnType<typeof createDummyAirbrake> => {
  const { VITE_AIRBRAKE_PROJECT_ID, VITE_AIRBRAKE_PROJECT_KEY } = import.meta.env;
  if (!VITE_AIRBRAKE_PROJECT_ID || !VITE_AIRBRAKE_PROJECT_KEY) {
    console.warn('[Airbrake] VITE_AIRBRAKE_PROJECT_* is not configured. Using console-only dummy.');
    return createDummyAirbrake();
  }
  const projectId = Number(VITE_AIRBRAKE_PROJECT_ID);
  if (Number.isNaN(projectId)) {
    console.warn('[Airbrake] VITE_AIRBRAKE_PROJECT_ID is invalid. Using console-only dummy.');
    return createDummyAirbrake();
  }
  console.info(`[Airbrake] Notifier enabled (${import.meta.env.MODE}).`);
  return new Notifier({
    environment: import.meta.env.MODE === 'production' ? 'production' : 'staging',
    projectId,
    projectKey: VITE_AIRBRAKE_PROJECT_KEY
  });
};

const airbrake = createAirbrakeFromEnv();
app.provide('airbrake', airbrake);
setAirbrake(airbrake);

if (import.meta.env.PROD) {
  app.config.errorHandler = (err, vm, info) => {
    airbrake.notify({
      error: err instanceof Error ? err : String(err),
      params: { info: info }
    });
  };
  window.addEventListener('error', (event) => {
    airbrake.notify({
      error: event.error,
      params: { event }
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    airbrake.notify({
      error: event.reason,
      params: { event }
    });
  });
  // Create an instance of the Mixpanel object, your token is already added to this snippet
  mixpanel.init('9e33d18212960b11f934a62e9cd9f19a', {
    autocapture: false,
    record_sessions_percent: 10, //records 1% of all sessions
    record_idle_timeout_ms: 1800000, //End a replay capture after 30mins of inactivity
    ignore_dnt: true,
    api_host: 'https://track.nextbase-teamcloud.com',
    persistence: 'localStorage'
  });
  app.provide('mixpanel', mixpanel);
} else {
  console.log('Mixpanel is disabled in development mode');
  // create dummy mixpanel instance
  const mixpanel = {
    track: (event: string, properties: Record<string, unknown>) => {
      console.log(`[Mixpanel] Tracking event: ${event}`, properties);
    },
    identify: (userId: string) => {
      console.log(`[Mixpanel] Identifying user: ${userId}`);
    },
    people: {
      set_once: (_properties: Record<string, unknown>) => {}
    },
    reset: () => {}
  };
  app.provide('mixpanel', mixpanel);
}
app.use(Toast, toastOptions);
app.use(VCalendar, {});
// セッション確認の結果に関わらずルーターを設定してアプリを起動
app.use(router);
app.use(pinia);
app.runWithContext(() => {
  setApiToast(useToast());
});
app.mount('#app');
